/* ==========================================================================
   SkinTalk.mx - Header JavaScript
   Custom elements: <sticky-header>, <header-drawer>
   Scroll detection, show/hide, transparent-to-opaque on homepage,
   mobile drawer open/close, mega menu hover intent.
   ========================================================================== */


/* ==========================================================================
   STICKY HEADER
   ========================================================================== */

if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.header = this.querySelector('.header');
      this.stickyType = this.dataset.stickyType || 'none';
      this.lastScrollY = 0;
      this.headerHeight = 0;
      this.isHidden = false;
      this.isScrolled = false;

      this.onScrollHandler = SkinTalk.throttle(this.onScroll.bind(this), 100);
    }

    connectedCallback() {
      this.setupNavHoverIntent();
      this.setupMobileSearch();

      if (this.stickyType === 'none') return;

      this.headerHeight = this.offsetHeight;
      this.classList.add('header-wrapper--sticky');

      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      window.addEventListener('resize', () => {
        this.headerHeight = this.offsetHeight;
      }, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    onScroll() {
      const scrollY = window.scrollY;
      const scrollingDown = scrollY > this.lastScrollY;
      const scrolledPast = scrollY > this.headerHeight;

      // Toggle scrolled state (shadow + opaque bg)
      if (scrolledPast && !this.isScrolled) {
        this.classList.add('header-wrapper--scrolled');
        this.isScrolled = true;
      } else if (!scrolledPast && this.isScrolled) {
        this.classList.remove('header-wrapper--scrolled');
        this.isScrolled = false;
      }

      // Sticky behavior
      if (this.stickyType === 'on-scroll-up') {
        if (scrollingDown && scrolledPast && !this.isHidden) {
          this.hide();
        } else if (!scrollingDown && this.isHidden) {
          this.show();
        }
      }
      // 'always' type stays visible, no hide needed

      this.lastScrollY = scrollY;
    }

    hide() {
      this.classList.add('header-wrapper--hidden');
      this.isHidden = true;
      this.closeAllMenus();
    }

    show() {
      this.classList.remove('header-wrapper--hidden');
      this.isHidden = false;
    }

    /* ------- Mega Menu Hover Intent ------- */
    setupNavHoverIntent() {
      const navItems = this.querySelectorAll('[data-has-submenu]');
      let hoverTimer = null;
      const HOVER_DELAY = 100;
      const LEAVE_DELAY = 250;

      navItems.forEach((item) => {
        const toggle = item.querySelector('.header__nav-link--toggle');
        const backdrop = this.querySelector('[data-header-backdrop]');

        item.addEventListener('mouseenter', () => {
          clearTimeout(hoverTimer);
          hoverTimer = setTimeout(() => {
            this.closeAllMenus();
            if (toggle) toggle.setAttribute('aria-expanded', 'true');
            if (backdrop) backdrop.classList.add('is-visible');
          }, HOVER_DELAY);
        });

        item.addEventListener('mouseleave', () => {
          clearTimeout(hoverTimer);
          hoverTimer = setTimeout(() => {
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
            if (backdrop) backdrop.classList.remove('is-visible');
          }, LEAVE_DELAY);
        });

        // Keyboard support
        if (toggle) {
          toggle.addEventListener('click', () => {
            const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
            this.closeAllMenus();
            toggle.setAttribute('aria-expanded', String(!isExpanded));
            if (backdrop) backdrop.classList.toggle('is-visible', !isExpanded);
          });

          toggle.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
              toggle.setAttribute('aria-expanded', 'false');
              if (backdrop) backdrop.classList.remove('is-visible');
              toggle.focus();
            }
          });
        }
      });

      // Close on backdrop click
      const backdrop = this.querySelector('[data-header-backdrop]');
      if (backdrop) {
        backdrop.addEventListener('click', () => {
          this.closeAllMenus();
          backdrop.classList.remove('is-visible');
        });
      }
    }

    closeAllMenus() {
      this.querySelectorAll('.header__nav-link--toggle').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
      const backdrop = this.querySelector('[data-header-backdrop]');
      if (backdrop) backdrop.classList.remove('is-visible');
    }

    /* ------- Mobile Search Toggle ------- */
    setupMobileSearch() {
      const searchToggles = document.querySelectorAll('[data-search-toggle]');
      const searchOverlay = document.getElementById('SearchOverlay');
      const searchCloseButtons = document.querySelectorAll('[data-search-close]');

      if (!searchOverlay) return;

      searchToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const isMobile = window.innerWidth < 990;

          if (isMobile) {
            searchOverlay.setAttribute('aria-hidden', 'false');
            SkinTalk.lockBodyScroll();
            const input = searchOverlay.querySelector('[data-search-input]');
            if (input) setTimeout(() => input.focus(), 100);
          } else {
            // Desktop: toggle inline search bar
            const searchBar = this.querySelector('[data-search-bar]');
            if (searchBar) {
              const isActive = searchBar.classList.toggle('is-active');
              toggle.setAttribute('aria-expanded', String(isActive));
              if (isActive) {
                const input = searchBar.querySelector('[data-search-input]');
                if (input) setTimeout(() => input.focus(), 100);
              }
            }
          }
        });
      });

      searchCloseButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          searchOverlay.setAttribute('aria-hidden', 'true');
          SkinTalk.unlockBodyScroll();

          // Also close desktop inline search
          const searchBar = this.querySelector('[data-search-bar]');
          if (searchBar) {
            searchBar.classList.remove('is-active');
            const toggle = this.querySelector('.header__search-toggle');
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
          }
        });
      });

      // Escape key closes search
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (searchOverlay.getAttribute('aria-hidden') === 'false') {
            searchOverlay.setAttribute('aria-hidden', 'true');
            SkinTalk.unlockBodyScroll();
          }
          const searchBar = this.querySelector('[data-search-bar]');
          if (searchBar && searchBar.classList.contains('is-active')) {
            searchBar.classList.remove('is-active');
            const toggle = this.querySelector('.header__search-toggle');
            if (toggle) {
              toggle.setAttribute('aria-expanded', 'false');
              toggle.focus();
            }
          }
        }
      });
    }
  }

  customElements.define('sticky-header', StickyHeader);
}


/* ==========================================================================
   HEADER DRAWER (Mobile Menu)
   ========================================================================== */

if (!customElements.get('header-drawer')) {
  class HeaderDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.closeBtn = this.querySelector('[data-drawer-close]');
      this.triggerElement = null;
    }

    connectedCallback() {
      // Open triggers
      document.querySelectorAll('[data-header-drawer-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          this.triggerElement = toggle;
          this.open();
        });
      });

      // Close on overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Close button
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Escape key
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open() {
      this.setAttribute('aria-hidden', 'false');
      SkinTalk.lockBodyScroll();
      SkinTalk.publish('drawer:open', { type: 'menu' });

      // Focus trap
      const panel = this.querySelector('.header-drawer__panel');
      if (panel) {
        setTimeout(() => {
          SkinTalk.trapFocus(panel, this.closeBtn);
        }, 100);
      }

      // Update hamburger aria
      if (this.triggerElement) {
        this.triggerElement.setAttribute('aria-expanded', 'true');
      }
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      SkinTalk.unlockBodyScroll();
      SkinTalk.removeTrapFocus();
      SkinTalk.publish('drawer:close', { type: 'menu' });

      // Restore focus to trigger
      if (this.triggerElement) {
        this.triggerElement.setAttribute('aria-expanded', 'false');
        this.triggerElement.focus();
      }
    }
  }

  customElements.define('header-drawer', HeaderDrawer);
}
